import React from "react";
import { Button, dialog, InfiniteScroll, Progress } from "nq-component";
import {
  countObjectUseCase,
  deleteObjectUseCase,
  findObjectUseCase,
  upsertUseCase,
} from "../../../../usecases/object";
import withRouter from "../../../../withRouter";
import Search from "../../../../components/Search";
import BaseListPage from "../../../../base/BaseListPage";
import InputFactory from "../../../../components/InputFactory";
import { toDisplayCase } from "nq-component";
import NavBar from "../../../../components/navbar";
import Table from "../../../../components/Table";
import CollapseView from "./components/CollapseView";
import DialogModal from "../../../../components/Dialog";
import RightSidebar from "../../../../components/RightSidebar";
import PullOutPresenter from "./PullOutPresenter";

class PullOutPage extends BaseListPage {
  constructor(props) {
    super(props);
    this.presenter = new PullOutPresenter(
      this,
      findObjectUseCase(),
      countObjectUseCase(),
      deleteObjectUseCase(),
      upsertUseCase()
    );
    this.state = {
      objects: [],
      selected: [],
      loading: true,
      total: 0,
      count: 0,
      range: "",
      showMobileFilters: false,
    };
  }

  getCollectionName() {
    return "backup_pullouts";
  }

  closeDialog() {
    dialog.close();
  }

  onClickImport() {
    this.presenter.onClickImport();
  }

  onClickExport() {
    this.presenter.onClickExport();
  }

  onChangeFilter(type, value, field) {
    const where = {};
    switch (type) {
      case "Pointer":
        if (Object.keys(value).length > 0) {
          where[field] = { id: value.id };
        }
        break;
      case "Boolean":
        where[field] = value;
        break;
      default:
        where[field] = { $regex: value, $options: "i" };
    }
    this.searchSubmit(where);
  }

  onChangeDate(where) {
    this.presenter.onChangeDate(where);
    this.setState({ range: where });
  }

  onClickAdd() {
    this.presenter.onClickAdd();
  }

  onClickAdjust(index) {
    this.presenter.onClickAdjust(index);
  }

  onClickView(index) {
    this.presenter.onClickView(index);
  }

  onClickDelete(index) {
    dialog.fire({
      html: (
        <DialogModal
          icon="bi bi-exclamation-octagon-fill"
          iconColor="red"
          title="Confirm Deletion"
          message="This will remove the selected data from the system. Are you sure want to continue?"
          isCancelButton={true}
          onSubmit={() => this.presenter.onClickDelete(index)}
          onClose={() => dialog.close()}
        />
      ),
      footer: false,
    });
  }
  onCollapse(index) {
    const pofb = this.state.objects[index];
    return (
      <CollapseView
        pofb={pofb}
        onAdjust={() => this.onClickAdjust(index)}
        onView={() => this.onClickView(index)}
      />
    );
  }

  formatFields(fields) {
    const { component, itemName, quantity, created, storeId, ...rest } = fields;

    return {
      component: { type: "String", label: "Component" },
      quantity: { type: "String", label: "Quantity" },
      uom: { type: "String", label: "UOM" },
      created: { type: "String", label: "Date Created" },
      ...rest,
    };
  }

  formatObjects(objects) {
    return objects.map((object) => {
      const { component, quantity, created, storeId, ...rest } = object || {};

      const formattedDate = created
        ? new Intl.DateTimeFormat("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          }).format(new Date(created))
        : "";

      return {
        component: object?.component?.itemName,
        quantity: object?.quantity,
        uom: object?.component?.uom?.name,
        created: formattedDate ? `${formattedDate}` : "",
        ...rest,
      };
    });
  }

  onClickExport() {
    this.presenter.onClickExport();
  }

  render() {
    const schema = this.getSchema(this.getCollectionName());
    const { objects, selected, count, loading } = this.state;

    if (!schema) return <Progress />;
    return (
      <>
        <NavBar />
        <RightSidebar
          isVisible={this.state.showMobileFilters}
          onClose={() => this.setState({ showMobileFilters: false })}
          onDateChange={this.onChangeDate.bind(this)}
          range={this.state.range}
        />
        <div className="overflow-auto">
          <InfiniteScroll
            className="h-100"
            loadMore={this.loadMore.bind(this)}
            hasMore={!loading && count > objects.length}
          >
            <div className="p-3 p-lg-4">
              <div className="d-flex justify-content-between align-items-center">
                <h1
                  className="fw-bold text-capitalize"
                  style={{ color: "#82c14a" }}
                >
                  {schema.label || toDisplayCase(this.getCollectionName())}
                </h1>
              </div>
              <div className="d-flex mt-3">
                {Object.keys(schema.filters || {}).map((field) => {
                  let { type, ...options } = schema.filters[field];
                  return (
                    <InputFactory
                      key={field}
                      className="ms-1"
                      type={type}
                      field={field}
                      where={{}}
                      onChange={this.onChangeFilter.bind(this, type)}
                      {...options}
                    />
                  );
                })}
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <Search
                  schemas={this.getSchemas()}
                  className="mt-3"
                  onSubmit={this.searchSubmit.bind(this)}
                  fields={schema.fields}
                />
                <div className="text-nowrap mt-3">
                  <i
                    className="bi bi-filter fs-1"
                    onClick={() => this.setState({ showMobileFilters: true })}
                    style={{ color: "#83c14a" }}
                  ></i>
                </div>
              </div>
              <Table
                fields={this.formatFields(schema.fields)}
                objects={this.formatObjects(objects)}
                selectable
                collapsable
                excludeFields={Object.keys(schema.fields).reduce(
                  (acc, key) => {
                    const options = schema.fields[key];
                    if (options.read === false) {
                      acc.push(key);
                    }
                    switch (options._type || options.type) {
                      case "Relation":
                      case "Array":
                      case "Object":
                      case "File":
                        acc.push(key);
                        break;
                      default:
                    }
                    return acc;
                  },
                  ["acl", "password", "adjustedBy"]
                )}
                selected={selected}
                onSelect={this.onSelect.bind(this)}
                onSelectAll={this.onSelectAll.bind(this)}
                onCollapse={this.onCollapse.bind(this)}
                progress={loading}
                onClickItem={this.onClickItem.bind(this)}
                className="mt-3"
              />
            </div>
          </InfiniteScroll>
        </div>
        <div className="position-fixed bottom-0 end-0 m-4">
          <Button
            className="btn shadow-sm"
            onClick={this.onClickAdd.bind(this)}
            style={{
              width: "50px",
              height: "50px",
              borderRadius: "25px",
              backgroundColor: "#82c14a",
              color: "white",
            }}
          >
            <i className="bi bi-plus-lg" />
          </Button>
        </div>
      </>
    );
  }
}

export default withRouter(PullOutPage);
